/**
 * Configurazione API Queries
 * Guest App, iCal Sync, and System Settings
 * Tables: guest_app_config, ical_sync, avvisi
 */

import { supabase, getCurrentUserId } from '../client';
import type { 
  GuestAppConfig, GuestAppConfigInsert, GuestAppConfigUpdate,
  ICalSync, ICalSyncInsert, ICalSyncUpdate,
  Avviso, AvvisoInsert, AvvisoUpdate,
  QueryOptions 
} from '@/types';
import { DatabaseTable } from '@/types/database.types';

// ============================================================
// GUEST APP CONFIG - GET
// ============================================================

export async function getGuestAppConfig(userId?: string) {
  const uid = userId || await getCurrentUserId();
  if (!uid) throw new Error('User not authenticated');
  
  const { data, error } = await supabase
    .from(DatabaseTable.GUEST_APP_CONFIG)
    .select('*')
    .eq('user_id', uid)
    .maybeSingle();

  return { data: data as GuestAppConfig | null, error };
}

// ============================================================
// GUEST APP CONFIG - POST/PATCH
// ============================================================

export async function createGuestAppConfig(config: GuestAppConfigInsert, userId?: string) {
  const uid = userId || await getCurrentUserId();
  if (!uid) throw new Error('User not authenticated');
  
  const { data, error } = await supabase
    .from(DatabaseTable.GUEST_APP_CONFIG)
    .insert({ ...config, user_id: uid })
    .select()
    .single();

  return { data: data as GuestAppConfig | null, error };
}

export async function updateGuestAppConfig(id: string, updates: GuestAppConfigUpdate, userId?: string) {
  const uid = userId || await getCurrentUserId();
  if (!uid) throw new Error('User not authenticated');
  
  const { data, error } = await supabase
    .from(DatabaseTable.GUEST_APP_CONFIG)
    .update(updates)
    .eq('id', id)
    .eq('user_id', uid)
    .select()
    .single();

  return { data: data as GuestAppConfig | null, error };
}

// Upsert - create if not exists, update if exists
export async function upsertGuestAppConfig(config: Partial<GuestAppConfigInsert>, userId?: string) {
  const uid = userId || await getCurrentUserId();
  if (!uid) throw new Error('User not authenticated');
  
  const { data: existing } = await getGuestAppConfig(uid);
  
  if (existing) {
    return updateGuestAppConfig(existing.id, config, uid);
  } else {
    return createGuestAppConfig(config as GuestAppConfigInsert, uid);
  }
}

// ============================================================
// ICAL SYNC - GET
// ============================================================

export async function getICalSyncs(options?: QueryOptions & { userId?: string }) {
  const userId = options?.userId || await getCurrentUserId();
  if (!userId) throw new Error('User not authenticated');
  
  let query = supabase
    .from(DatabaseTable.ICAL_SYNC)
    .select('*, properties(nome)', { count: options?.count })
    .eq('user_id', userId);

  if (options?.filters?.struttura_id) {
    query = query.eq('struttura_id', options.filters.struttura_id);
  }

  if (options?.order) {
    query = query.order(options.order.column, { ascending: options.order.ascending ?? true });
  }

  if (options?.limit) query = query.limit(options.limit);

  const { data, error, count } = await query;
  return { data: data as ICalSync[] | null, error, count };
}

export async function getICalSyncById(id: string, userId?: string) {
  const uid = userId || await getCurrentUserId();
  if (!uid) throw new Error('User not authenticated');
  
  const { data, error } = await supabase
    .from(DatabaseTable.ICAL_SYNC)
    .select('*')
    .eq('id', id)
    .eq('user_id', uid)
    .single();

  return { data: data as ICalSync | null, error };
}

// ============================================================
// ICAL SYNC - POST/PATCH/DELETE
// ============================================================

export async function createICalSync(sync: ICalSyncInsert, userId?: string) {
  const uid = userId || await getCurrentUserId();
  if (!uid) throw new Error('User not authenticated');
  
  const { data, error } = await supabase
    .from(DatabaseTable.ICAL_SYNC)
    .insert({ ...sync, user_id: uid })
    .select()
    .single();

  return { data: data as ICalSync | null, error };
}

export async function updateICalSync(id: string, updates: ICalSyncUpdate, userId?: string) {
  const uid = userId || await getCurrentUserId();
  if (!uid) throw new Error('User not authenticated');
  
  const { data, error } = await supabase
    .from(DatabaseTable.ICAL_SYNC)
    .update({ ...updates, ultimo_sync: new Date().toISOString() })
    .eq('id', id)
    .eq('user_id', uid)
    .select()
    .single();

  return { data: data as ICalSync | null, error };
}

export async function deleteICalSync(id: string, userId?: string) {
  const uid = userId || await getCurrentUserId();
  if (!uid) throw new Error('User not authenticated');
  
  const { error } = await supabase
    .from(DatabaseTable.ICAL_SYNC)
    .delete()
    .eq('id', id)
    .eq('user_id', uid);

  return { error };
}

// Force sync trigger
export async function triggerICalSync(id: string, userId?: string) {
  return updateICalSync(id, { ultimo_sync: new Date().toISOString() }, userId);
}

// ============================================================
// AVVISI - GET
// ============================================================

export async function getAvvisi(options?: QueryOptions & { userId?: string }) {
  const userId = options?.userId || await getCurrentUserId();
  if (!userId) throw new Error('User not authenticated');
  
  let query = supabase
    .from(DatabaseTable.AVVISI)
    .select('*', { count: options?.count })
    .eq('user_id', userId);

  // Filter by active (scadenza in futuro)
  if (options?.filters?.attivo) {
    const today = new Date().toISOString();
    query = query.gte('scadenza', today);
  }

  if (options?.order) {
    query = query.order(options.order.column, { ascending: options.order.ascending ?? true });
  } else {
    query = query.order('scadenza', { ascending: true });
  }

  if (options?.limit) query = query.limit(options.limit);

  const { data, error, count } = await query;
  return { data: data as Avviso[] | null, error, count };
}

export async function getAvvisoById(id: string, userId?: string) {
  const uid = userId || await getCurrentUserId();
  if (!uid) throw new Error('User not authenticated');
  
  const { data, error } = await supabase
    .from(DatabaseTable.AVVISI)
    .select('*')
    .eq('id', id)
    .eq('user_id', uid)
    .single();

  return { data: data as Avviso | null, error };
}

export async function getAvvisiAttivi(userId?: string) {
  return getAvvisi({
    userId,
    filters: { attivo: true },
    limit: 10,
  });
}

// ============================================================
// AVVISI - POST/PATCH/DELETE
// ============================================================

export async function createAvviso(avviso: AvvisoInsert, userId?: string) {
  const uid = userId || await getCurrentUserId();
  if (!uid) throw new Error('User not authenticated');
  
  const { data, error } = await supabase
    .from(DatabaseTable.AVVISI)
    .insert({ ...avviso, user_id: uid })
    .select()
    .single();

  return { data: data as Avviso | null, error };
}

export async function updateAvviso(id: string, updates: AvvisoUpdate, userId?: string) {
  const uid = userId || await getCurrentUserId();
  if (!uid) throw new Error('User not authenticated');
  
  const { data, error } = await supabase
    .from(DatabaseTable.AVVISI)
    .update(updates)
    .eq('id', id)
    .eq('user_id', uid)
    .select()
    .single();

  return { data: data as Avviso | null, error };
}

export async function deleteAvviso(id: string, userId?: string) {
  const uid = userId || await getCurrentUserId();
  if (!uid) throw new Error('User not authenticated');
  
  const { error } = await supabase
    .from(DatabaseTable.AVVISI)
    .delete()
    .eq('id', id)
    .eq('user_id', uid);

  return { error };
}

// Mark as read
export async function markAvvisoAsRead(id: string, userId?: string) {
  return updateAvviso(id, { letto: true }, userId);
}
